import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Controller } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";

import Dialog from "components/dialog";
import {
  TASK_STATUSES,
  TASK_STATUS_CANCELLED,
  TASK_STATUS_COMPLETED,
  TASK_STATUS_IN_PROGRESS,
  TASK_STATUS_TODO
} from "constants/staff-tasks";

type BulkUpdateStatusModalProps = {
  isOpen: boolean;
  selectedCount: number;
  onClose: () => void;
  onSave: (status: number) => void;
  isLoading?: boolean;
};

export default function BulkUpdateStatusModal({
  isOpen,
  selectedCount,
  onClose,
  onSave,
  isLoading = false
}: BulkUpdateStatusModalProps) {
  const { control, handleSubmit, reset } = useForm<{ status: number }>({
    defaultValues: {
      status: TASK_STATUS_TODO
    }
  });

  const onSubmit = useCallback(
    (data: { status: number }) => {
      onSave(data.status);
      reset();
    },
    [onSave, reset]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Dialog
      title={`Update Status for ${selectedCount} task${selectedCount !== 1 ? "s" : ""}`}
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ p: 2 }}>
          <Controller
            name="status"
            control={control}
            rules={{ required: "Status is required" }}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  <MenuItem value={TASK_STATUS_TODO}>
                    {TASK_STATUSES.getFromCode(TASK_STATUS_TODO)}
                  </MenuItem>
                  <MenuItem value={TASK_STATUS_IN_PROGRESS}>
                    {TASK_STATUSES.getFromCode(TASK_STATUS_IN_PROGRESS)}
                  </MenuItem>
                  <MenuItem value={TASK_STATUS_COMPLETED}>
                    {TASK_STATUSES.getFromCode(TASK_STATUS_COMPLETED)}
                  </MenuItem>
                  <MenuItem value={TASK_STATUS_CANCELLED}>
                    {TASK_STATUSES.getFromCode(TASK_STATUS_CANCELLED)}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
